/**
 * Install state: whether PadLab is running inside browser chrome or as an installed app,
 * what launched it, and what that means for navigation.
 *
 * The distinction matters more on iOS than anywhere else. A Home Screen launch gets the full
 * display, a persistent storage bucket the browser is far less willing to evict, and — most
 * importantly — no back button. Code that navigates away from the scope in browser mode is
 * merely rude; in standalone mode it strands the user in a window they cannot leave without
 * force-quitting. Everything here exists to make that difference explicit rather than
 * something each screen has to remember.
 */

import { computed, signal, type ReadonlySignal } from '../core/signal';
import { readDisplayMode, readLegacyStandalone, type DisplayMode } from './capabilities';
import { createLogger, tag } from '../core/log';

const log = createLogger('standalone');

// MARK: - Durable flags
//
// localStorage rather than IndexedDB on purpose: these are three booleans read during the
// first frame, and an async read would mean rendering the browser-mode layout and then
// flipping it. Safari throws from localStorage in some privacy configurations, so every
// access is guarded and a failure degrades to "not remembered" rather than to a crash.

const FLAG_PREFIX = 'padlab.flag.';

export function readStoredFlag(name: string): string | null {
  try {
    return window.localStorage.getItem(FLAG_PREFIX + name);
  } catch {
    return null;
  }
}

export function writeStoredFlag(name: string, value: string): void {
  try {
    window.localStorage.setItem(FLAG_PREFIX + name, value);
  } catch {
    // Private browsing, a full quota, or a storage policy. The feature that wrote the flag
    // has to keep working without it; the only cost is that the decision is not remembered.
    log.warn('Preference could not be stored', { flag: tag(name) });
  }
}

export function clearStoredFlag(name: string): void {
  try {
    window.localStorage.removeItem(FLAG_PREFIX + name);
  } catch {
    log.warn('Preference could not be cleared', { flag: tag(name) });
  }
}

const FLAG_INSTALLED_LAUNCH = 'installed-launch';
const FLAG_INSTALLED_ONBOARDING = 'installed-onboarding-done';

// MARK: - Display mode

const displayModeSignal = signal<DisplayMode>(readDisplayMode());
const legacyStandaloneSignal = signal<boolean>(readLegacyStandalone());

/** The `display-mode` the browser reports right now. */
export const displayMode: ReadonlySignal<DisplayMode> = displayModeSignal;

/**
 * True when PadLab owns the whole screen: any installed display mode, or iOS Safari's
 * `navigator.standalone`, which is the only signal old WebKit gives for a Home Screen launch.
 */
export const isStandalone: ReadonlySignal<boolean> = computed(
  () => displayModeSignal.value !== 'browser' || legacyStandaloneSignal.value,
);

const WATCHED_MODES: DisplayMode[] = [
  'window-controls-overlay',
  'fullscreen',
  'standalone',
  'minimal-ui',
];

function observeDisplayMode(): () => void {
  const disposers: Array<() => void> = [];
  const refresh = (): void => {
    displayModeSignal.value = readDisplayMode();
    legacyStandaloneSignal.value = readLegacyStandalone();
    recordInstalledLaunch();
  };

  for (const mode of WATCHED_MODES) {
    const query = window.matchMedia(`(display-mode: ${mode})`);
    query.addEventListener('change', refresh);
    disposers.push(() => query.removeEventListener('change', refresh));
  }

  // Chromium fires this the moment the user completes an install, while the page that
  // triggered it is still open in a tab.
  const onInstalled = (): void => {
    writeStoredFlag(FLAG_INSTALLED_LAUNCH, 'true');
    refresh();
  };
  window.addEventListener('appinstalled', onInstalled);
  disposers.push(() => window.removeEventListener('appinstalled', onInstalled));

  return () => {
    for (const dispose of disposers) dispose();
  };
}

/**
 * Attached at import time because install state is read during the first frame and there is
 * no point in the app existing without it. The disposer is returned for tests, which need to
 * be able to run several instances in one document.
 */
export const stopObservingDisplayMode = observeDisplayMode();

// MARK: - Installed history

function recordInstalledLaunch(): void {
  if (!isStandalone.peek()) return;
  if (readStoredFlag(FLAG_INSTALLED_LAUNCH) === 'true') return;
  writeStoredFlag(FLAG_INSTALLED_LAUNCH, 'true');
}

/**
 * True once PadLab has been opened from the Home Screen at least once — which is how the
 * install guide knows to stop offering itself when the same person later opens the site in
 * Safari. There is no API that answers "is this site installed"; this flag is the closest
 * honest approximation, and it is deliberately one-way.
 */
export function hasLaunchedInstalled(): boolean {
  return readStoredFlag(FLAG_INSTALLED_LAUNCH) === 'true';
}

recordInstalledLaunch();

// MARK: - Launch intent

export type LaunchSource = 'browser' | 'home-screen' | 'shortcut' | 'share-target' | 'deep-link';

export interface LaunchIntent {
  readonly source: LaunchSource;
  /** A manifest shortcut target: `new-project`, `voice-lab`, `sequencer`. */
  readonly screen: string | null;
  /** A deep link into a project the device already holds. */
  readonly projectId: string | null;
  /** A deep link to a public sound pack. */
  readonly packId: string | null;
  /** The claim token for files the OS share sheet handed to the service worker. */
  readonly shareToken: string | null;
  /** True when the share target ran but the payload could not be read. */
  readonly shareFailed: boolean;
}

const SCREEN_TARGETS = new Set(['new-project', 'voice-lab', 'sequencer', 'library', 'settings']);

function readLaunchIntent(): LaunchIntent {
  const params = new URL(window.location.href).searchParams;

  const rawScreen = params.get('screen');
  const screen = rawScreen !== null && SCREEN_TARGETS.has(rawScreen) ? rawScreen : null;
  const projectId = params.get('project');
  const packId = params.get('pack');
  const rawShare = params.get('share');
  const shareFailed = rawShare === 'failed';
  const shareToken = rawShare !== null && !shareFailed ? rawShare : null;

  const installed = isStandalone.peek();
  let source: LaunchSource;
  if (shareToken !== null || shareFailed) source = 'share-target';
  else if (screen !== null) source = 'shortcut';
  else if (projectId !== null || packId !== null) source = 'deep-link';
  else source = installed ? 'home-screen' : 'browser';

  return { source, screen, projectId, packId, shareToken, shareFailed };
}

/**
 * Captured once, at import, before anything can rewrite the URL. A shortcut or a share
 * token is a one-shot instruction; reading it lazily would mean losing it to the first
 * `history.replaceState` any screen performs.
 */
export const launchIntent: LaunchIntent = readLaunchIntent();

/**
 * Strips the one-shot launch parameters from the address bar so a reload — or a Home Screen
 * relaunch, which reuses the last URL on iOS — does not re-import the same shared file or
 * bounce the user back into the shortcut's screen.
 */
export function clearLaunchQuery(): void {
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of ['screen', 'share', 'project', 'pack']) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (!changed) return;
  const query = url.searchParams.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${query === '' ? '' : `?${query}`}${url.hash}`,
  );
}

// MARK: - Installed-app onboarding

const onboardingDone = signal<boolean>(readStoredFlag(FLAG_INSTALLED_ONBOARDING) === 'true');

/**
 * True the first time PadLab is opened from the Home Screen, and never again.
 *
 * This is a different path from the browser-mode first run on purpose. A person who has just
 * installed the app has already seen the marketing page and the install guide; what they have
 * *not* seen is that the app now works offline, that it can hold the microphone, and where
 * the stop-all control lives. That is what the installed tour covers.
 */
export const installedOnboardingPending: ReadonlySignal<boolean> = computed(
  () => isStandalone.value && !onboardingDone.value,
);

export function completeInstalledOnboarding(): void {
  onboardingDone.value = true;
  writeStoredFlag(FLAG_INSTALLED_ONBOARDING, 'true');
}

/** The settings screen's "show the tour again" control. */
export function resetInstalledOnboarding(): void {
  onboardingDone.value = false;
  clearStoredFlag(FLAG_INSTALLED_ONBOARDING);
}

// MARK: - Install-state-aware navigation

export interface NavigationPolicy {
  /** Browser mode only: there is somewhere to install *to*. */
  readonly showInstallAffordance: boolean;
  /** Full-bleed instrument chrome with no page header of its own. */
  readonly showAppChrome: boolean;
  /**
   * In standalone mode there is no back button and no address bar, so a link that leaves the
   * scope has to open a browser window instead of navigating this one.
   */
  readonly externalLinksOpenInBrowser: boolean;
  /** Standalone windows keep their own history stack; the app owns back and forward. */
  readonly ownsHistory: boolean;
}

export const navigationPolicy: ReadonlySignal<NavigationPolicy> = computed(() => {
  const installed = isStandalone.value;
  return {
    showInstallAffordance: !installed,
    showAppChrome: installed,
    externalLinksOpenInBrowser: installed,
    ownsHistory: installed,
  };
});

function isInScope(url: URL): boolean {
  if (url.origin !== window.location.origin) return false;
  const scope = new URL('./', window.location.href).pathname;
  return url.pathname.startsWith(scope);
}

/**
 * Opens a URL the right way for the current install state. In a tab this is an ordinary
 * navigation; in an installed window an out-of-scope URL is handed to the browser so the app
 * window is never left showing somebody else's page with no way back.
 */
export function openExternal(url: string): void {
  const target = new URL(url, window.location.href);
  if (isInScope(target)) {
    window.location.assign(target.href);
    return;
  }
  const opened = window.open(target.href, '_blank', 'noopener,noreferrer');
  // Popup blockers refuse `window.open` outside a user gesture. Falling back to a same-window
  // navigation is still better than a link that silently does nothing.
  if (opened === null) window.location.assign(target.href);
}

/**
 * Delegated click handling for anchors inside `root`. Any link that leaves PadLab's scope is
 * routed through `openExternal`, so a marketplace licence link or a support page cannot trap
 * an installed window. Returns a disposer.
 */
export function bindExternalLinks(root: HTMLElement): () => void {
  const onClick = (event: MouseEvent): void => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!navigationPolicy.peek().externalLinksOpenInBrowser) return;

    const anchor = (event.target as Element | null)?.closest('a');
    if (anchor === null || anchor === undefined) return;
    if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;

    const href = anchor.getAttribute('href');
    if (href === null || href.startsWith('#')) return;

    let target: URL;
    try {
      target = new URL(href, window.location.href);
    } catch {
      return;
    }
    if (target.protocol !== 'http:' && target.protocol !== 'https:') return;
    if (isInScope(target)) return;

    event.preventDefault();
    openExternal(target.href);
  };

  root.addEventListener('click', onClick);
  return () => root.removeEventListener('click', onClick);
}
